import ReferralFormWrapper from "src/components/forms/ReferralFormWrapper/ReferralFormWrapper";

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Stack,
  VStack,
} from "@chakra-ui/react";
import { IReferralFormWrapperProps } from "src/components/forms/ReferralFormWrapper/ReferralFormWrapper";

import styled from "@emotion/styled";
import { IMainContactFormFields } from "src/types/generated/contentful";
import { BlockRenderer } from "../../molecules/BlockRenderer";

const FormStyles = styled.div<{ theme?: any }>`
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  label,
  p {
    ${({ theme }) =>
      `color: ${theme.colors["brandPrimary"]["500"]} !important;`}
    text-align: left;
  }
`;

interface IMainContactFormProps extends IMainContactFormFields {
  defaultContent?: React.ReactElement | undefined;
  confirmationMessage?: React.ReactElement;
  submitButtonTitle?: string;
  verificationMessage?: string;
}
export default function ReferralForm(data: IMainContactFormProps) {
  const {
    title,
    content: formTopContent,
    defaultContent,
    beforeSubmitContent,
    formBottomContent,
    successMessage,
    verificationMessage,
    submitButtonTitle,
  } = data;

  const formDefaultHeader = defaultContent ?? (
    <Flex direction="column" alignItems="center" p={8} maxW={900}>
      <Heading as="h2" textAlign="center" size="4xl">
        {"Ready to Get Started?"}
      </Heading>

      <Box textAlign="center" w={["100%", "75%", "50%"]}>
        Get FREE Medicare help today! Please enter your information and a
        licensed agent will contact you.
      </Box>
    </Flex>
  );
  const successMessageBlock = !successMessage ? (
    <Flex direction="column" alignItems="center" p={8} maxW={900}>
      <Heading as="h2" textAlign="center">
        Thank You
      </Heading>

      <Box textAlign="center">
        We have everything we need to get started finding you the best Medicare
        plan to fit your needs. A Licensed Sales Agent will be contacting you
        soon.
      </Box>
    </Flex>
  ) : (
    <Flex direction="column" alignItems="center" p={8} maxW={900}>
      <Box textAlign="center">
        <BlockRenderer block={successMessage} layoutId="successMessage" />
      </Box>
    </Flex>
  );

  return (
    <ReferralFormWrapper id="ReferralForm">
      {({ formik, submitted }: IReferralFormWrapperProps): JSX.Element => (
        <FormStyles>
          <Stack id="getStartedNow" alignItems={"center"}>
            {submitted ? (
              successMessageBlock
            ) : (
              <>
                {formTopContent ? (
                  <BlockRenderer
                    block={formTopContent}
                    layoutId="customContactForm"
                    key="customContactForm"
                  />
                ) : (
                  <>{formDefaultHeader}</>
                )}

                <Stack maxW={500} spacing={6} mt={7}>
                  {/*
            <label htmlFor="email">Email</label>
            <FormField name="email" placeholder="Enter Your Email" />
            */}
                  {formik?.errors?.email}
                  <FormControl
                    variant="floating"
                    id="email"
                    isRequired
                    onBlur={formik?.handleBlur}
                    onChange={formik?.handleChange}
                    isInvalid={Boolean(
                      formik?.touched?.email && formik?.errors?.email
                    )}
                  >
                    <Input
                      placeholder=" "
                      type="email"
                      id="email"
                      size="lg"
                      onChange={formik?.handleChange}
                      value={formik?.values?.email}
                    />
                    <FormLabel htmlFor="email">Email</FormLabel>
                    <FormErrorMessage fontWeight="semibold" color="red.600">
                      {formik?.errors?.email ||
                        `Please enter a valid email address.`}
                    </FormErrorMessage>
                  </FormControl>

                  <SimpleGrid columns={[1, 2]} spacing={6} m={3}>
                    <FormControl
                      variant="floating"
                      id="firstName"
                      isRequired
                      isInvalid={Boolean(
                        formik?.touched?.firstName && formik?.errors?.firstName
                      )}
                    >
                      <Input
                        placeholder=" "
                        id="firstName"
                        type="text"
                        size="lg"
                        onBlur={formik?.handleBlur}
                        onChange={formik?.handleChange}
                        value={formik?.values?.firstName}
                      />
                      <FormLabel htmlFor="firstName">First Name</FormLabel>
                      <FormErrorMessage fontWeight="semibold" color="red.600">
                        Please enter your first name.
                      </FormErrorMessage>
                    </FormControl>

                    <FormControl
                      variant="floating"
                      id="lastName"
                      isRequired
                      isInvalid={Boolean(
                        formik?.touched?.lastName && formik?.errors?.lastName
                      )}
                    >
                      <Input
                        placeholder=" "
                        id="lastName"
                        type="text"
                        size="lg"
                        onBlur={formik?.handleBlur}
                        onChange={formik?.handleChange}
                        value={formik?.values?.lastName}
                      />
                      <FormLabel htmlFor="lastName">Last Name</FormLabel>
                      <FormErrorMessage fontWeight="semibold" color="red.600">
                        Please enter your last name.
                      </FormErrorMessage>
                    </FormControl>
                  </SimpleGrid>

                  {formik?.status ? (
                    <Alert status="error" borderRadius={10}>
                      <AlertIcon />
                      There was an error processing your request
                    </Alert>
                  ) : null}

                  <VStack>
                    {verificationMessage ? (
                      <Checkbox
                        id="verification"
                        name="verification"
                        onBlur={formik?.handleBlur}
                        onChange={formik?.handleChange}
                        isChecked={formik?.values?.verification}
                        isInvalid={Boolean(
                          formik?.touched?.verification &&
                            formik?.errors?.verification
                        )}
                        gap={1}
                        mt={2}
                      >
                        {verificationMessage}
                      </Checkbox>
                    ) : null}
                    <Box mt={4}>
                      &nbsp;
                      {/*JSON.stringify(form, null, 2)*/}
                      {beforeSubmitContent ? (
                        <BlockRenderer
                          block={beforeSubmitContent}
                          layoutId="beforeSubmitContent"
                          key="beforeSubmitContent"
                        />
                      ) : null}
                    </Box>
                    <Center>
                      {/*disabled={formik?.isSubmitting}*/}
                      <Button
                        type="submit"
                        variant={"brand-solid-accent"}
                        cursor="pointer"
                        disabled={formik?.isSubmitting}
                        isLoading={formik?.isSubmitting}
                        loadingText="Submitting"
                      >
                        {submitButtonTitle || "Submit"}
                      </Button>
                    </Center>

                    {formBottomContent ? (
                      <Box mt={4}>
                        <BlockRenderer
                          block={formBottomContent}
                          layoutId="formBottomContent"
                          key="formBottomContent"
                        />
                      </Box>
                    ) : null}
                  </VStack>
                </Stack>
              </>
            )}
          </Stack>
        </FormStyles>
      )}
    </ReferralFormWrapper>
  );
}
